import React, { useState, useRef, useEffect } from 'react';
import { urls } from './config.dev';
import { toast } from 'react-toastify';
import SuccessDialog from './SuccessDialog';

function TestResultModal({ patient, clinicDetails, token, onClose, onSubmit, totalLabTests, testsToBeDone = [] }) {
  const [results, setResults] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printSuccess, setPrintSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Set initial results with current date/time in Kampala timezone
    const now = new Date();
    const options = {
      timeZone: 'Africa/Kampala',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(now);
    
    // Only set the initial date if results is empty
    if (!results.trim()) {
      setResults(`Date: ${formattedDate}\n\n`);
      
      // Set cursor position after the date
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
          textareaRef.current.focus();
        }
      }, 0);
    }

    // Fetch lab templates from backend
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetch(urls.fetchLabTemplates, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setTemplates(data.data);
        } else {
          throw new Error('Failed to fetch templates');
        }
      } else {
        throw new Error('Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load lab templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const formatLabTests = (tests) => {
    if (!tests) return '';
    return tests.replace(/Lab Test:\s*/g, '');
  };

  const handleInputChange = (event) => {
    setResults(event.target.value);
  };

  const handleTemplateSelect = (template) => {
    const textarea = textareaRef.current;
    const currentValue = textarea.value;
    const cursorPosition = textarea.selectionStart;
    
    // Insert the template at the cursor position
    const newValue = 
      currentValue.substring(0, cursorPosition) + 
      template.report + 
      currentValue.substring(cursorPosition);
    
    setResults(newValue);
    
    // Set cursor position after the inserted template
    setTimeout(() => {
      textarea.selectionStart = cursorPosition + template.report.length;
      textarea.selectionEnd = cursorPosition + template.report.length;
      textarea.focus();
    }, 0);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(urls.submitlabresults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_id: patient.file_id,
          contact_id: patient.contact_id,
          results: results,
          totalLabTests: totalLabTests,
          token: token
        })
      });
      
      if (response.ok) {
        onSubmit();
        setSuccess(true);
        setLoading(false);
      } else {
        throw new Error('Failed to submit lab results');
      }
    } catch (error) {
      console.error('Error submitting lab results:', error);
      setError('Failed to submit results. Please try again.');
      setLoading(false);
    }
  };

  const handlePrintConfirmation = async (shouldPrint) => {
    if (!shouldPrint) {
      onClose();
      return;
    }

    setPrinting(true);
    try {
      await handlePrint();
      setPrintSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Printing error:', error);
      setError('Failed to generate PDF. Results were saved successfully.');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrint = async () => {
    const printData = {
      clinicName: clinicDetails?.clinic_name || 'No Clinic Name Available',
      contact: clinicDetails?.owners_contact || 'No Contact Available',
      location: `${clinicDetails?.sub_county || ''}, ${clinicDetails?.district || ''}`,
      patientName: `${patient.first_name} ${patient.last_name}`,
      patientAge: patient.age,
      patientSex: patient.sex,
      laboratoryTests: formatLabTests(patient.lab_tests),
      laboratoryResults: results.trim().split('\n').map(result => result.trim()),
    };

    const response = await fetch(urls.pdflab2, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(printData)
    });

    if (!response.ok) {
      throw new Error(`Failed to generate PDF: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    toast.success('PDF report generated successfully!');
  };

  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modalContent: {
      backgroundColor: '#fff',
      width: '95%',
      maxWidth: '1200px',
      height: '90vh',
      padding: '0',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
      position: 'relative',
      display: 'flex',
      overflow: 'hidden',
    },
    leftPanel: {
      width: '35%',
      backgroundColor: '#f8f9fa',
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    rightPanel: {
      width: '65%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    clinicHeader: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '10px',
      textAlign: 'center',
    },
    clinicName: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#2c3e50',
      margin: 0,
      textTransform: 'uppercase',
    },
    clinicLocation: {
      fontSize: '14px',
      color: '#7f8c8d',
      margin: 0,
      textTransform: 'uppercase',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#3498db',
      margin: '10px 0',
    },
    patientInfoContainer: {
      backgroundColor: '#ffffff',
      padding: '15px 20px',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      margin: '10px',
      border: '1px solid #eee',
    },
    patientInfo: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '10px',
    },
    infoItem: {
      fontSize: '14px',
      margin: '5px 0',
    },
    infoLabel: {
      fontWeight: '600',
      color: '#555',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '15px',
      paddingBottom: '8px',
      borderBottom: '1px solid #eee',
      textAlign: 'center',
      textTransform: 'uppercase',
    },
    templateSection: {
      padding: '20px',
      flex: 1,
      overflowY: 'auto',
    },
    templateList: {
      maxHeight: '200px',
      overflowY: 'auto',
      border: '1px solid #ddd',
      borderRadius: '5px',
      marginBottom: '15px',
    },
    templateItem: {
      padding: '10px 15px',
      borderBottom: '1px solid #eee',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      ':hover': {
        backgroundColor: '#f0f7ff',
      },
    },
    textAreaContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      overflow: 'hidden',
    },
    textArea: {
      width: '100%',
      flex: 1,
      padding: '15px',
      border: '1px solid #ddd',
      borderRadius: '5px',
      resize: 'none',
      fontSize: '14px',
      fontFamily: "'Roboto Mono', 'Consolas', 'Monaco', monospace",
      lineHeight: '1.6',
      backgroundColor: '#fff',
      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      minHeight: '200px',
      marginBottom: '15px',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '15px',
      padding: '15px 20px',
      backgroundColor: '#f8f9fa',
      borderTop: '1px solid #e0e0e0',
    },
    submitButton: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      backgroundColor: '#3498db',
      color: 'white',
      fontWeight: '500',
      transition: 'all 0.2s',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '150px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    cancelButton: {
      padding: '10px 20px',
      border: '1px solid #ddd',
      borderRadius: '5px',
      cursor: 'pointer',
      backgroundColor: '#fff',
      color: '#555',
      fontWeight: '500',
      transition: 'all 0.2s',
      fontSize: '14px',
      minWidth: '100px',
    },
    disabledButton: {
      opacity: 0.6,
      cursor: 'not-allowed',
      backgroundColor: '#95a5a6',
    },
    loadingText: {
      display: 'inline-block',
      marginLeft: '10px',
    },
    errorText: {
      color: '#e74c3c',
      fontSize: '14px',
      marginTop: '10px',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100px',
    },
  };

  return (
    <>
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          {/* Left Panel - Patient Details and Templates */}
          <div style={styles.leftPanel}>
            <div style={styles.header}>
              <div style={styles.clinicHeader}>
                <div>
                  <h3 style={styles.clinicName}>{clinicDetails?.clinic_name || 'Medical Laboratory'}</h3>
                  <p style={styles.clinicLocation}>
                    {clinicDetails?.sub_county || ''}, {clinicDetails?.district || ''}
                  </p>
                </div>
              </div>
            </div>
            
            <div style={styles.patientInfoContainer}>
              <div style={styles.sectionTitle}>Patient Information</div>
              <div style={styles.patientInfo}>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Name:</span> {`${patient.first_name} ${patient.last_name}`}
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Age/Sex:</span> {patient.age}/{patient.sex}
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>File ID:</span> {patient.file_id}
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Tests:</span> {formatLabTests(patient.lab_tests)}
                </div>
              </div>
            </div>

            <div style={styles.templateSection}>
              <div style={styles.sectionTitle}>Test Templates</div>
              
              {templatesLoading ? (
                <div style={styles.loadingContainer}>
                  <div>Loading templates...</div>
                </div>
              ) : (
                <>
                  <div style={styles.templateList}>
                    {templates.map((template) => (
                      <div 
                        key={template.id}
                        style={styles.templateItem}
                        onClick={() => handleTemplateSelect(template)}
                      >
                        {template.report_name}
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ marginTop: '20px' }}>
                    <div style={styles.sectionTitle}>Instructions</div>
                    <ul style={{ 
                      fontSize: '13px', 
                      color: '#555', 
                      paddingLeft: '20px',
                      lineHeight: '1.6'
                    }}>
                      <li>Click a template to insert it at cursor position</li>
                      <li>Review results carefully before submission</li>
                      <li>Use proper formatting for readability</li>
                      <li>Results will be saved to patient's record</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Panel - Text Area and Buttons */}
          <div style={styles.rightPanel}>
            <div style={styles.textAreaContainer}>
              <label style={styles.sectionTitle} htmlFor="resultsTextarea">
                Laboratory Results
              </label>
              <textarea
                id="resultsTextarea"
                ref={textareaRef}
                style={styles.textArea}
                value={results}
                onChange={handleInputChange}
                placeholder="Enter detailed laboratory results here..."
                spellCheck="false"
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={{
                  ...styles.cancelButton,
                  ...(loading && styles.disabledButton)
                }}
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.submitButton,
                  ...(loading && styles.disabledButton),
                  ':hover': !loading && !results.trim() ? {} : { backgroundColor: '#2980b9' }
                }}
                onClick={handleSubmit}
                disabled={loading || !results.trim()}
              >
                {loading ? 'Submitting...' : 'Submit Results'}
                {loading && <span style={styles.loadingText}></span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      {success && (
        <SuccessDialog 
          onClose={() => {
            setSuccess(false);
            onClose();
          }}
          onPrint={() => handlePrintConfirmation(true)}
          onNoPrint={() => handlePrintConfirmation(false)}
          printing={printing}
          printSuccess={printSuccess}
        />
      )}
    </>
  );
}

export default TestResultModal;